import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, QrCode, Upload, Check, Clock, Lock, Send, Utensils } from 'lucide-react';
import { moduleApi } from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import { resolveLogoUrl } from '../../../lib/resolveLogoUrl';
import { generateTechnicalSheetPdf } from '../../../lib/menusConvivesTechnicalSheetPdfGenerator';
import { generateClientRecapPdf } from '../../../lib/menusConvivesClientRecapPdfGenerator';
import { COLLECTE_STATUS_LABELS } from '../collecteStatus';
import { useConfirm } from '../../ui/ConfirmProvider';
import { EmailHistoryModal } from '../../ui/EmailHistoryModal';
import { pillAccent, PILL_ACCENT_STYLE, pillSecondary, pillGhost, pillWarn } from '../../ui/pills';
import { ConviveEmailSettingsModal } from './ConviveEmailSettingsModal';
import { GuestDetailModal } from './GuestDetailModal';
import { ConvivesTable } from './ConvivesTable';
import { ConvivesImportModal } from './ConvivesImportModal';
import { ConvivesEnvoiModal, ModeEnvoi } from './ConvivesEnvoiModal';
import { ConvivesCloturerModal } from './ConvivesCloturerModal';
import { ClotureDateModal } from './ClotureDateModal';
import { FormuleRetenueCard, CollecteReponsesCard, ComptageCuisineCard, FormuleInfos } from './ConvivesSuiviCards';
import { useConvivesActions } from './useConvivesActions';
import { ConvivesFilters, EMPTY_FILTERS, Guest, guestAllergies, guestRestrictions } from './guestFilters';
import { OrganisateurAccesCard, useOrganisateurAcces } from './organisateur/OrganisateurAccesCard';
import { OrganisateurAccesModal } from './organisateur/OrganisateurAccesModal';

interface EvenementielEvent { id: string; type: 'PRIVÉ' | 'PROFESSIONNEL'; first_name?: string; last_name?: string; company_name?: string; start_time: string; num_people?: number; }
interface Collecte {
    status: string; production_locked_at: string | null; responses_closed_at?: string | null;
    responses_close_date?: string | null; prewarning_date?: string | null; prewarning_sent_at?: string | null; close_notify_enabled?: boolean | number;
    menu_ids?: string[]; guest_choice_mode?: 'convive' | 'organisateur'; fixed_dish_ids?: Record<string, string>;
    dish_quotas?: Record<string, number>;
}

const eventLabel = (e: EvenementielEvent) => e.type === 'PRIVÉ' ? `${e.first_name || ''} ${e.last_name || ''}`.trim() : (e.company_name || '');
const fmtDateLongue = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }); } catch { return ''; }
};
/** Jours entiers d'ici la date (AAAA-MM-JJ), à midi pour ignorer les fuseaux. */
const joursAvant = (d: string): number => Math.ceil((new Date(`${d}T12:00:00`).getTime() - Date.now()) / 86400000);

/**
 * Page d'atterrissage de Menus & Convives pour un événement (maquette 18b, refonte design lot 9) : le
 * fichier sert à inviter, le choix vient du convive. En-tête, bandeau de clôture, la formule retenue et
 * la collecte des réponses, le tableau des convives, le comptage cuisine. Toute la logique (import,
 * envois par lots, clôture + validation, retrait) est dans `useConvivesActions`, inchangée.
 * La configuration du menu reste sur la page dédiée (route .../menu) — retour d'Emeni, les deux sur un
 * seul écran faisaient « brouillon ».
 */
export function ConvivesSuiviPage() {
    const { eventId } = useParams<{ eventId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const confirmAction = useConfirm();
    const [event, setEvent] = React.useState<EvenementielEvent | null>(null);
    const [collecte, setCollecte] = React.useState<Collecte | null>(null);
    const [formule, setFormule] = React.useState<Pick<FormuleInfos, 'menus' | 'items'>>({ menus: [], items: [] });
    const [loading, setLoading] = React.useState(true);
    const [includeAllergies, setIncludeAllergies] = React.useState(true);
    const [occupe, setOccupe] = React.useState<'' | 'fiche' | 'recap'>('');
    const [erreurDoc, setErreurDoc] = React.useState('');
    const [modale, setModale] = React.useState<null | 'import' | 'envoi' | 'cloturer' | 'date' | 'emails' | 'organisateur'>(null);
    const [prevenirOrga, setPrevenirOrga] = React.useState(true);
    const [modeEnvoi, setModeEnvoi] = React.useState<ModeEnvoi>('invitation');
    const [filters, setFilters] = React.useState<ConvivesFilters>(EMPTY_FILTERS);
    const [historyGuest, setHistoryGuest] = React.useState<Guest | null>(null);
    const [detailGuest, setDetailGuest] = React.useState<Guest | null>(null);

    const id = eventId || '';
    const actions = useConvivesActions(id);
    const orga = useOrganisateurAcces(id);

    const load = React.useCallback(() => {
        if (!eventId) return;
        Promise.all([
            moduleApi.getEvenementielEvent(eventId),
            moduleApi.getEvenementielCollecte(eventId),
            moduleApi.getMenus().catch(() => []),
            moduleApi.getMenuItems().catch(() => []),
        ]).then(([ev, col, m, mi]) => {
            setEvent(ev);
            setCollecte(col);
            const ids: string[] = Array.isArray(col?.menu_ids) ? col.menu_ids : [];
            setFormule({ menus: (Array.isArray(m) ? m : []).filter((x: any) => ids.includes(x.id)), items: Array.isArray(mi) ? mi : [] });
        }).finally(() => setLoading(false));
    }, [eventId]);
    React.useEffect(() => { load(); }, [load]);

    const entete = () => ({
        eventLabel: event ? eventLabel(event) : '',
        eventDate: event?.start_time || '',
        issuerName: user?.company_name || undefined,
        logoUrl: resolveLogoUrl(user?.logoUrl || user?.logo_url) || null,
        brandColor: user?.theme_color || undefined,
    });

    // Fiche technique cuisine — total par plat choisi + allergies/intolérances déclarées (Emeni, 2026-08-22).
    const ficheTechnique = async () => {
        if (!eventId || !event) return;
        setOccupe('fiche'); setErreurDoc('');
        try {
            const sheet: any = await moduleApi.getEvenementielCollecteTechnicalSheet(eventId);
            await generateTechnicalSheetPdf({ ...entete(), totalResponded: sheet?.total_responded || 0, dishes: Array.isArray(sheet?.dishes) ? sheet.dishes : [], restrictionsAllergies: Array.isArray(sheet?.restrictions_allergies) ? sheet.restrictions_allergies : [], includeAllergies });
        } catch (e: any) { setErreurDoc(e?.message || 'Erreur lors de la génération de la fiche technique.'); } finally { setOccupe(''); }
    };
    // Fiche récap CLIENT (Jour J) — ce que les convives ont choisi (Emeni, 2026-08-22).
    const recapClient = async () => {
        if (!eventId || !event) return;
        setOccupe('recap'); setErreurDoc('');
        try {
            const recap: any = await moduleApi.getEvenementielCollecteClientRecap(eventId);
            await generateClientRecapPdf({ ...entete(), guests: Array.isArray(recap?.guests) ? recap.guests.map((g: any) => ({ name: g.name, tableNumber: g.table_number, dishesByCategory: g.dishes_by_category || {}, allergies: g.allergies || [], restrictions: g.restrictions || [] })) : [] });
        } catch (e: any) { setErreurDoc(e?.message || 'Erreur lors de la génération du récap.'); } finally { setOccupe(''); }
    };

    const retirer = async (g: Guest) => {
        // Retirer un convive qui a répondu efface sa réponse : irréversible, donc confirmation qui le nomme.
        if (!(await confirmAction({ title: 'Retirer le convive ?', description: <><strong>{g.last_name?.toUpperCase()} {g.first_name}</strong> sera retiré de la liste de cet événement{g.responded_at ? ', et sa réponse au menu avec lui' : ''}.</>, confirmLabel: 'Retirer le convive' }))) return;
        actions.removeGuest(g.id);
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;
    if (!event || !eventId || !collecte) return <p className="text-sm text-content-muted">Événement introuvable.</p>;

    const guests = actions.guests;
    const c = actions.counts;
    const contraintes = guests.filter((g) => guestAllergies(g).length > 0 || guestRestrictions(g).length > 0).length;
    const dernierEnvoi = guests.reduce<string | null>((m, g) => (g.invitation_sent_at && (!m || g.invitation_sent_at > m) ? g.invitation_sent_at : m), null);
    const closeDate = collecte.responses_close_date || '';
    const jours = closeDate ? joursAvant(closeDate) : null;
    const cloturee = !!collecte.responses_closed_at;
    // Un plat n'est proposé au convive que si son quota est renseigné (un nombre) ou « Illimité » (-1).
    // Zéro plat autorisé = lien vide pour tout le monde : la carte de la formule le dit.
    const platsAutorises = Object.values(collecte.dish_quotas || {}).filter((q) => Number(q) === -1 || Number(q) > 0).length;
    const validee = !!collecte.production_locked_at;
    const eventDay = String(event.start_time || '').slice(0, 10);
    const ouvrirEnvoi = (mode: ModeEnvoi) => { setModeEnvoi(mode); setModale('envoi'); };
    const nombreEnvoi = modeEnvoi === 'relance' ? c.unresponded : c.notYetInvited;

    return (
        // Pas de largeur maximale : c'est une liste de plusieurs centaines de lignes, elle n'a rien à gagner à
        // être bridée au milieu d'un écran d'ordinateur (Emeni, 2026-08-24).
        <div className="space-y-3">
            <button type="button" onClick={() => navigate('/menus-convives')} className="flex items-center gap-1.5 text-xs font-bold text-content-muted hover:text-content">
                <ArrowLeft size={14} /> Retour à la liste
            </button>

            <div className="rounded-[14px] border border-border bg-surface overflow-hidden">
                {/* En-tête (maquette 18b) */}
                <div className="flex items-start justify-between gap-3.5 flex-wrap px-5 py-4 border-b border-border">
                    <div className="min-w-0">
                        <div className="text-[15px] font-semibold text-content">Menus &amp; Convives</div>
                        <p className="mt-[3px] text-[12.5px] text-content-muted">
                            {eventLabel(event)} · {c.total} convive{c.total > 1 ? 's' : ''} importé{c.total > 1 ? 's' : ''} · {c.notYetInvited === 0 && c.total > 0 ? `${c.total} invitations envoyées` : `${c.total - c.notYetInvited} invitation${c.total - c.notYetInvited > 1 ? 's' : ''} envoyée${c.total - c.notYetInvited > 1 ? 's' : ''}`}
                            {' · '}<span className="inline-flex items-center h-[18px] px-1.5 rounded bg-surface-soft border border-border-soft text-[10px] font-bold uppercase tracking-[.05em] align-middle">{COLLECTE_STATUS_LABELS[collecte.status] || collecte.status}</span>
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* La configuration du menu est accessible depuis l'en-tete, a tout statut : c'est le
                            premier geste d'une privatisation qui vient d'etre creee, et on ne doit pas avoir a
                            passer par Suivi privatisation pour le faire (Emeni, 18/09). */}
                        <Link to={`/menus-convives/${eventId}/menu`} className={pillGhost(34)} title="Formule, date de cloture et message aux convives"><Utensils size={14} /> Configurer le menu</Link>
                        <Link to={`/menus-convives/${eventId}/qr-codes`} className={pillGhost(34)} title="QR codes des tables"><QrCode size={14} /> QR codes</Link>
                        <button type="button" onClick={() => setModale('emails')} className={pillGhost(34)} title="Personnaliser les courriels des convives"><Mail size={14} /> Emails</button>
                        <button type="button" onClick={() => setModale('import')} className={pillSecondary(34)}>
                            <Upload size={14} /> {c.total > 0 ? 'Remplacer le fichier' : 'Importer le fichier'}
                        </button>
                        {c.notYetInvited > 0 && (
                            <button type="button" onClick={() => ouvrirEnvoi('invitation')} className={pillSecondary(34)} disabled={actions.sending}>
                                <Send size={14} /> Envoyer les invitations ({c.notYetInvited})
                            </button>
                        )}
                        {cloturee ? (
                            <span className="inline-flex items-center gap-[7px] h-[34px] px-[14px] rounded-full bg-state-ok-soft border border-state-ok/30 text-state-ok text-[12.5px] font-semibold whitespace-nowrap"><Lock size={14} /> Collecte clôturée</span>
                        ) : (
                            <button type="button" onClick={() => setModale('cloturer')} className={pillAccent(34)} style={PILL_ACCENT_STYLE} disabled={actions.closing || actions.sending || c.total === 0}>
                                <Check size={14} /> Clôturer la collecte
                            </button>
                        )}
                    </div>
                </div>

                <div className="px-5 py-4 space-y-3.5">
                    {/* Bandeau de clôture : la date est réglée ici, et annoncée dans chaque courriel. */}
                    {cloturee ? (
                        <div className="flex items-center gap-3 px-[15px] py-[13px] rounded-xl bg-state-ok-soft border border-state-ok/30">
                            <span className="w-[30px] h-[30px] shrink-0 rounded-lg bg-surface border border-state-ok/30 text-state-ok flex items-center justify-center"><Lock size={15} /></span>
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-bold text-state-ok">Réponses clôturées{validee ? ' — liste validée' : ''}</div>
                                <div className="mt-px text-[12px] text-state-ok">Les convives ne peuvent plus répondre ; le compte est parti en cuisine et le plan de table se consulte en mode terrain.</div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3 px-[15px] py-[13px] rounded-xl bg-state-warn-soft border border-state-warn/30 flex-wrap">
                            <span className="w-[30px] h-[30px] shrink-0 rounded-lg bg-surface border border-state-warn/30 text-state-warn flex items-center justify-center"><Clock size={15} /></span>
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-bold text-state-warn">
                                    {closeDate ? `Clôture des réponses le ${fmtDateLongue(closeDate)}, en fin de journée` : 'Aucune date de clôture réglée'}
                                </div>
                                <div className="mt-px text-[12px] text-state-warn text-pretty">
                                    {closeDate
                                        ? `${jours !== null && jours > 0 ? `Dans ${jours} jour${jours > 1 ? 's' : ''}. ` : jours === 0 ? "C'est aujourd'hui. " : 'Date passée — la fermeture se fait le lendemain matin. '}La date est réglée ici, et annoncée dans chaque courriel envoyé aux convives.`
                                        : 'Sans date, vous clôturez à la main quand vous le souhaitez. Réglez-la ici : elle sera annoncée dans chaque courriel envoyé aux convives.'}
                                </div>
                            </div>
                            <button type="button" onClick={() => setModale('date')} className={pillWarn(32)}>{closeDate ? 'Changer la date' : 'Régler la date'}</button>
                        </div>
                    )}

                    <div className="grid grid-cols-[repeat(auto-fit,minmax(310px,1fr))] gap-3 items-start">
                        <FormuleRetenueCard eventId={eventId} couverts={event.num_people || 0}
                            infos={{ ...formule, guestChoiceMode: collecte.guest_choice_mode === 'organisateur' ? 'organisateur' : 'convive', fixedDishIds: collecte.fixed_dish_ids && typeof collecte.fixed_dish_ids === 'object' ? collecte.fixed_dish_ids : {}, validee, platsAutorises }} />
                        <CollecteReponsesCard s={{ importes: c.total, invites: c.total - c.notYetInvited, reponses: c.responded, silencieux: c.unresponded, nonInvites: c.notYetInvited, envoyeLe: dernierEnvoi }} />
                    </div>

                    <OrganisateurAccesCard actifs={orga.actifs} onGerer={() => setModale('organisateur')} />

                    {(actions.error || erreurDoc) && <p className="text-[12px] text-state-danger font-semibold">{actions.error || erreurDoc}</p>}
                    {actions.sendProgress && (
                        <p className="text-[12px] text-content-muted">
                            {actions.sendProgress.done}/{actions.sendProgress.total} traités — {actions.sendProgress.sent} envoyé(s){actions.sendProgress.failed > 0 ? `, ${actions.sendProgress.failed} échoué(s)` : ''}
                            {actions.sendProgress.failed > 0 && actions.sendProgress.lastError && <span className="block text-state-danger font-semibold mt-0.5">Dernière erreur : {actions.sendProgress.lastError}</span>}
                        </p>
                    )}

                    {actions.loading ? (
                        <div className="flex justify-center py-6"><Loader2 className="animate-spin text-content-muted" size={18} /></div>
                    ) : c.total === 0 ? (
                        <div className="rounded-xl border border-dashed border-border p-6 text-center">
                            <p className="text-[12.5px] text-content-muted">Aucun convive pour l'instant — importez le fichier du client pour lancer les invitations.</p>
                        </div>
                    ) : (
                        <>
                            {c.unresponded > 0 && !cloturee && (
                                <div className="flex justify-end">
                                    <button type="button" onClick={() => ouvrirEnvoi('relance')} className={pillSecondary(32)} disabled={actions.sending}>
                                        <Send size={13} /> Relancer les {c.unresponded} sans réponse
                                    </button>
                                </div>
                            )}
                            <ConvivesTable guests={guests} filters={filters} onChange={setFilters} sending={actions.sending}
                                onOpen={setDetailGuest} onSend={(g) => actions.resendOne(g.id)} onHistory={setHistoryGuest} onRemove={retirer} />
                        </>
                    )}

                    <ComptageCuisineCard reponses={c.responded} couvertsDevis={event.num_people || 0} contraintes={contraintes}
                        occupe={occupe} includeAllergies={includeAllergies} onIncludeAllergies={setIncludeAllergies}
                        onFicheTechnique={ficheTechnique} onRecap={recapClient} />
                </div>
            </div>

            {modale === 'import' && <ConvivesImportModal importing={actions.importing} onImport={actions.importRows} onClose={() => setModale(null)} />}
            {modale === 'envoi' && (
                <ConvivesEnvoiModal mode={modeEnvoi} nombre={nombreEnvoi} total={c.total - c.notYetInvited} evenement={eventLabel(event)} sending={actions.sending}
                    organisateur={orga.actifs.length ? { nom: orga.actifs.map((a) => a.name).join(' et '), coche: prevenirOrga, onChange: setPrevenirOrga } : undefined}
                    onConfirm={async () => {
                        setModale(null);
                        if (modeEnvoi === 'relance') await actions.remindUnresponded(); else await actions.sendAllInvitations();
                        // Le compte rendu à l'organisateur part une fois, après le dernier lot — jamais un courriel par lot de 25.
                        if (orga.actifs.length && prevenirOrga) moduleApi.prevenirOrganisateur(id, { mode: modeEnvoi, nombre: nombreEnvoi }).catch(() => undefined);
                    }}
                    onClose={() => setModale(null)} />
            )}
            {modale === 'cloturer' && (
                <ConvivesCloturerModal reponses={c.responded} silencieux={c.nonResponded} contraintes={contraintes} closing={actions.closing}
                    onConfirm={async () => { setModale(null); await actions.closeResponses(); load(); }} onClose={() => setModale(null)} />
            )}
            {modale === 'date' && (
                <ClotureDateModal eventId={eventId} eventDay={eventDay} prewarningSentAt={collecte.prewarning_sent_at}
                    initial={{ closeDate, prewarnDate: collecte.prewarning_date || '', closeNotify: collecte.close_notify_enabled === undefined || collecte.close_notify_enabled === null ? true : !!collecte.close_notify_enabled }}
                    onSaved={() => { setModale(null); load(); }} onClose={() => setModale(null)} />
            )}
            {modale === 'emails' && <ConviveEmailSettingsModal onClose={() => setModale(null)} />}
            {modale === 'organisateur' && <OrganisateurAccesModal eventId={id} info={orga.info} onChange={orga.recharger} onClose={() => setModale(null)} />}
            {historyGuest && (
                <EmailHistoryModal title={`Historique — ${historyGuest.first_name} ${historyGuest.last_name}`}
                    fetchHistory={async () => { const res: any = await moduleApi.getEvenementielGuestEmailHistory(eventId, historyGuest.id); return Array.isArray(res?.emails) ? res.emails : []; }}
                    onClose={() => setHistoryGuest(null)} />
            )}
            {detailGuest && <GuestDetailModal eventId={eventId} guestId={detailGuest.id} onClose={() => { setDetailGuest(null); actions.reload(); }} />}
        </div>
    );
}
